import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { FaPlus, FaTimes, FaTools, FaSave, FaSpinner } from "react-icons/fa";
import { useData } from "../../context/DataContext";
import ImageUpload from "../ImageUpload";
import { resolveMediaUrl } from "../../utils/mediaUrl";
import { SipCalendar, SipDropdown, SipInput, SipSearch, SipTextarea } from "../form/SipControls";

function AddPerawatan({ form, setForm, postData, setIsModalOpen, isModalOpen, reset }) {

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [isClosing, setIsClosing] = useState(false);
  const [searchKode, setSearchKode] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const{inventaris = [], getInventaris, petugas, getPetugas, gambar}=useData();
  const searchTimeoutRef = useRef(null);
  const hasInitializedRef = useRef(false);

  const resolveFotoUrl = (file) =>
    resolveMediaUrl(`${gambar}perawatan/`, typeof file === 'string' ? file : null) || file;

  useEffect(() => {
      getPetugas();
    }, []);

  useEffect(() => {
    // Panggil getInventaris dengan parameter kosong untuk mendapatkan semua data hanya sekali
    if (getInventaris && isModalOpen && !hasInitializedRef.current) {
      hasInitializedRef.current = true;
      getInventaris('').catch(error => {
        console.error('Error loading initial inventaris:', error);
      });
    }
    
    // Reset flag ketika modal ditutup
    if (!isModalOpen) {
      hasInitializedRef.current = false;
    }
  }, [isModalOpen, getInventaris]);

  // Fetch inventaris list for dropdown


  useEffect(() => {
    if (isModalOpen) {
      getInventaris();
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      // Restore body scroll when modal is closed
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to restore scroll on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  // Handle click outside modal to close
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && isModalOpen) {
        closeModal();
      }
    };

    if (isModalOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isModalOpen]);

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    
    if (type === 'file') {
      setForm(prev => ({
        ...prev,
        [name]: files[0]
      }));
    } else {
      setForm(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleImageChange = (file, fieldName = 'foto') => {
    setForm(prev => ({
      ...prev,
      [fieldName]: file
    }));

    if (errors[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    // Validasi inventaris_id
    if (!form.inventaris_id || form.inventaris_id === '') {
      newErrors.inventaris_id = 'Inventaris harus dipilih';
    }

    // Validasi jenis kerusakan
    if (!form.jenis_kerusakan || !form.jenis_kerusakan.trim()) {
      newErrors.jenis_kerusakan = 'Jenis kerusakan harus diisi';
    }

    // Validasi tindakan
    if (!form.tindakan || !form.tindakan.trim()) {
      newErrors.tindakan = 'Tindakan harus diisi';
    }

    // Validasi tanggal masuk
    if (!form.tgl_masuk || form.tgl_masuk === '') {
      newErrors.tgl_masuk = 'Tanggal masuk harus diisi';
    }

    // Validasi petugas
    if (!form.petugas || !form.petugas.trim()) {
      newErrors.petugas = 'Petugas harus diisi';
    }

    // Validasi status
    if (!form.status || form.status === '') {
      newErrors.status = 'Status harus dipilih';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const response = await postData();
      console.log('Response dari server:', response);
    } catch (error) {
      console.error('Error submitting form:', error);
      // Tampilkan pesan error yang lebih detail
      let errorMessage = 'Gagal menambahkan data perawatan. ';
      if (error.response) {
        // Error dari server
        errorMessage += `Server error: ${error.response.status} - ${error.response.data?.message || 'Unknown error'}`;
      } else if (error.request) {
        // Error jaringan
        errorMessage += 'Tidak dapat terhubung ke server. Periksa koneksi internet Anda.';
      } else {
        // Error lainnya
        errorMessage += error.message || 'Terjadi kesalahan yang tidak diketahui.';
      }
      
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  // Fungsi untuk mencari inventaris berdasarkan kode
  const searchInventaris = useCallback(async (kode) => {
    if (!kode.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    try {
      setLoadingSearch(true);
      await getInventaris(kode);
    } catch (error) {
      console.error('Error searching inventaris:', error);
      setSearchResults([]);
      setShowSearchResults(true); // Tetap tampilkan untuk menunjukkan pesan tidak ditemukan
    } finally {
      setLoadingSearch(false);
    }
  }, [getInventaris]);

  // Handler untuk perubahan input pencarian
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchKode(value);
    
    // Jika input kosong, sembunyikan hasil pencarian
    if (!value.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Debounce search
    searchTimeoutRef.current = setTimeout(() => {
      searchInventaris(value);
    }, 500);
  }, [searchInventaris]);

  // Handler untuk memilih inventaris dari hasil pencarian
  const setHandleInventaris = (selectedInventaris) => {
    if (!selectedInventaris || !selectedInventaris.id) {
      console.error('Invalid inventaris data:', selectedInventaris);
      return;
    }

    setForm(prev => ({
      ...prev,
      inventaris_id: selectedInventaris.id,
      aset: selectedInventaris.desc || selectedInventaris.kode || 'Tidak ada deskripsi'
    }));
    
    setSearchKode(selectedInventaris.kode || '');
    setShowSearchResults(false);
    
    // Clear error jika ada
    if (errors.inventaris_id) {
      setErrors(prev => ({
        ...prev,
        inventaris_id: ''
      }));
    }
  };

  // Update search results ketika inventaris dari context berubah
  useEffect(() => {
    if (inventaris && Array.isArray(inventaris) && searchKode.trim() && !loadingSearch) {
      setSearchResults(inventaris);
      setShowSearchResults(true);
    } else if (!searchKode.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  }, [inventaris, searchKode, loadingSearch]);

  // Cleanup timeout saat component unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Handle click outside to close search results
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.search-container')) {
        setShowSearchResults(false);
      }
    };

    if (showSearchResults) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSearchResults]);

  const closeModal = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsModalOpen(false);
      setIsClosing(false);
      setErrors({});
      // Reset search states
      setSearchKode('');
      setSearchResults([]);
      setShowSearchResults(false);
      // Reset form when closing
      reset()
    }, 300);
  };

  // Modal component to be rendered via portal
  const modalContent = isModalOpen && (
    <div 
      className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999] ${
        isClosing ? 'animate__animated animate__fadeOut animate__faster' : 'animate__animated animate__fadeIn animate__faster'
      }`}
      onClick={handleBackdropClick}
    >
      <div 
        className={`bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden relative ${
          isClosing ? 'animate__animated animate__zoomOut animate__faster' : 'animate__animated animate__zoomIn animate__faster'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="bg-primary px-6 py-4 flex items-center justify-between animate__animated animate__slideInDown animate__delay-1s">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg animate__animated animate__pulse animate__infinite animate__slower">
              <FaTools className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-white">Tambah Data Perawatan</h2>
          </div>
          <button
            onClick={closeModal}
            className="p-2 hover:bg-white/20 rounded-lg transition-all duration-300 hover:rotate-90"
          >
            <FaTimes className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Grid Layout for Form Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              
              {/* Pencarian Inventaris */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cari Inventaris berdasarkan Kode <span className="text-red-500">*</span>
                </label>
                <div className="relative search-container">
                  <div className="relative">
                    <SipSearch
                      value={searchKode}
                      onChange={handleSearchChange}
                      placeholder="Masukkan kode inventaris untuk mencari..."
                    />
                    {loadingSearch && (
                      <FaSpinner className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 animate-spin text-primary" />
                    )}
                  </div>
                  
                  {/* Hasil Pencarian */}
                  {showSearchResults && searchResults && searchResults.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border-2 border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                      {searchResults.map((item) => (
                        <div
                          key={item.id}
                          onClick={() => setHandleInventaris(item)}
                          className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-all duration-200"
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <p className="font-medium text-gray-900">{item.kode || 'N/A'}</p>
                              <p className="text-sm text-gray-600">{item.desc || 'Tidak ada deskripsi'}</p>
                            </div>
                            <div className="text-xs text-gray-500">
                              Klik untuk pilih
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Pesan jika tidak ada hasil */}
                  {showSearchResults && (!searchResults || searchResults.length === 0) && searchKode && !loadingSearch && (
                    <div className="absolute z-50 w-full mt-1 bg-white border-2 border-gray-200 rounded-xl shadow-lg p-4">
                      <p className="text-gray-500 text-center">Tidak ada inventaris ditemukan dengan kode "{searchKode}"</p>
                    </div>
                  )}
                </div>
                
                {/* Tampilkan inventaris yang dipilih */}
                {form.inventaris_id && form.aset && (
                  <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-800">
                      <span className="font-medium">Inventaris Dipilih:</span> {searchKode} - {form.aset}
                    </p>
                  </div>
                )}
                
                {errors.inventaris_id && (
                  <p className="text-red-500 text-sm mt-1 animate__animated animate__shakeX">{errors.inventaris_id}</p>
                )}
              </div>

              {/* Jenis Kerusakan */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Jenis Kerusakan <span className="text-red-500">*</span>
                </label>
                <SipInput
                  name="jenis_kerusakan"
                  value={form.jenis_kerusakan}
                  onChange={handleInputChange}
                  placeholder="Masukkan jenis kerusakan"
                />
                {errors.jenis_kerusakan && (
                  <p className="text-red-500 text-sm mt-1 animate__animated animate__shakeX">{errors.jenis_kerusakan}</p>
                )}
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status <span className="text-red-500">*</span>
                </label>
                <SipDropdown
                  name="status"
                  value={form.status}
                  onChange={handleInputChange}
                  placeholder="Pilih Status"
                  filter={false}
                  options={[
                    { label: "Dijadwalkan", value: "terjadwal" },
                    { label: "Dalam Proses", value: "proses" },
                    { label: "Selesai", value: "selesai" },
                  ]}
                />
                {errors.status && (
                  <p className="text-red-500 text-sm mt-1 animate__animated animate__shakeX">{errors.status}</p>
                )}
              </div>

              {/* Tindakan */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tindakan <span className="text-red-500">*</span>
                </label>
                <SipTextarea
                  name="tindakan"
                  value={form.tindakan}
                  onChange={handleInputChange}
                  placeholder="Masukkan tindakan yang dilakukan"
                  rows={3}
                />
                {errors.tindakan && (
                  <p className="text-red-500 text-sm mt-1 animate__animated animate__shakeX">{errors.tindakan}</p>
                )}
              </div>

              {/* Deskripsi */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Deskripsi
                </label>
                <SipTextarea
                  name="deskripsi"
                  value={form.deskripsi}
                  onChange={handleInputChange}
                  placeholder="Masukkan deskripsi tambahan"
                  rows={3}
                />
              </div>

              {/* Tanggal Masuk */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tanggal Masuk <span className="text-red-500">*</span>
                </label>
                <SipCalendar name="tgl_masuk" value={form.tgl_masuk} onChange={handleInputChange} />
                {errors.tgl_masuk && (
                  <p className="text-red-500 text-sm mt-1 animate__animated animate__shakeX">{errors.tgl_masuk}</p>
                )}
              </div>

              {/* Tanggal Selesai */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tanggal Selesai
                </label>
                <SipCalendar name="tgl_selesai" value={form.tgl_selesai} onChange={handleInputChange} />
              </div>

              {/* Petugas */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Petugas/Teknisi <span className="text-red-500">*</span>
                </label>
                <SipDropdown
                  name="petugas"
                  value={form.petugas}
                  onChange={handleInputChange}
                  placeholder="Pilih petugas"
                  options={(petugas || []).map((item) => ({
                    label: `${item.nama} - ${item.jabatan}`,
                    value: item.nama,
                  }))}
                />
              
                {errors.petugas && (
                  <p className="text-red-500 text-sm mt-1">{errors.petugas}</p>
                )}
              </div>

              {/* Keterangan */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Keterangan
                </label>
                <SipInput
                  name="ket"
                  value={form.ket}
                  onChange={handleInputChange}
                  placeholder="Masukkan keterangan"
                />
              </div>

              {/* Upload Foto */}
              <div className="md:col-span-2">
                <ImageUpload
                  label="Upload Foto"
                  name="foto"
                  value={resolveFotoUrl(form.foto)}
                  onChange={handleImageChange}
                  optional
                />
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex items-center justify-end gap-4 pt-6 border-t border-gray-200 ">
              <button
                type="button"
                onClick={closeModal}
                className="px-6 py-3 text-gray-700 bg-gray-100 border border-gray-300 rounded-xl hover:bg-gray-200 transition-all duration-300 font-medium hover:scale-105"
              >
                Batal
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-xl hover:from-primary/90 hover:to-secondary/90 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105"
              >
                {loading ? (
                  <>
                    <FaSpinner className="w-4 h-4 animate-spin" />
                    Menyimpan...
                  </>
                ) : (
                  <>
                    <FaSave className="w-4 h-4" />
                    Simpan Data
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Trigger Button */}
      <button 
        onClick={() => setIsModalOpen(true)}
        className="flex items-center justify-center gap-2 bg-primary text-white px-6 py-3 rounded-xl hover:bg-primary/90 transition-all duration-200 shadow-md hover:shadow-lg font-medium"
      >
        <FaPlus />
        Tambah Perawatan
      </button>

      {/* Modal rendered via portal */}
      {modalContent && createPortal(modalContent, document.body)}
    </>
  );
}

export default AddPerawatan;